/* eslint-disable no-underscore-dangle */
import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AppContextData } from '../../features/AppContext';
import {
  applyBrandFavicon,
  applyBrandDocumentTitle,
  applyBrandColors,
  getBrandLogoSrc,
  getDefaultBrandLogo,
  getOriginBrandConfig,
  resolveActiveOrgId
} from '../../utilities/brandLogo';
import { resolveEffectiveBrandName } from '../../languages';

export const ZINIER_LOGO_STYLE = {
  height: '32px',
  maxHeight: '32px',
  maxWidth: '160px',
  width: 'auto',
  objectFit: 'contain'
};

export const CUSTOM_LOGO_DEFAULT_STYLE = {
  height: 'auto',
  maxHeight: '56px',
  maxWidth: '240px',
  width: 'auto',
  objectFit: 'contain'
};

/**
 * Resolve differential logo styling between Zinier wordmark baseline and custom brand logos.
 * Supports explicit logoHeight, logoWidth, and logoStyle overrides from window.__BRAND.
 */
export const resolveLogoStyle = (src, orgId, passedStyle) => {
  const isZinier = src === getDefaultBrandLogo();
  if (isZinier) {
    return {
      ...ZINIER_LOGO_STYLE,
      ...passedStyle
    };
  }

  const brand = getOriginBrandConfig();
  const trimmedOrg = resolveActiveOrgId(orgId);
  const orgConfig = trimmedOrg && brand?.orgs && brand.orgs[trimmedOrg];
  const explicitHeight = orgConfig?.logoHeight || brand?.logoHeight;
  const explicitWidth = orgConfig?.logoWidth || brand?.logoWidth;
  const explicitStyle = orgConfig?.logoStyle || brand?.logoStyle || {};

  return {
    ...CUSTOM_LOGO_DEFAULT_STYLE,
    ...(explicitHeight ? { maxHeight: explicitHeight } : {}),
    ...(explicitWidth ? { maxWidth: explicitWidth } : {}),
    ...explicitStyle,
    ...passedStyle
  };
};

/**
 * Shared auth-screen logo. Resolves CDN / override via getBrandLogoSrc.
 * Falls back to the bundled Zinier wordmark if the remote asset fails to load.
 */
export function BrandLogo ({ className, style, onClick, alt, orgId: propOrgId }) {
  const context = useContext(AppContextData);
  const orgId = propOrgId ||
    context?.state?.org_id ||
    context?.state?.org_alias ||
    resolveActiveOrgId() ||
    '';
  const resolvedSrc = getBrandLogoSrc(orgId);
  const [src, setSrc] = useState(resolvedSrc);

  useEffect(() => {
    setSrc(resolvedSrc);
    applyBrandFavicon(orgId);
    applyBrandDocumentTitle(orgId);
    applyBrandColors(orgId);
  }, [resolvedSrc, orgId]);

  const computedStyle = resolveLogoStyle(src, orgId, style);
  const effectiveBrand = resolveEffectiveBrandName(orgId);
  const resolvedAlt = alt || (effectiveBrand ? effectiveBrand : 'Zinier');

  return (
    <img
      src={src}
      alt={resolvedAlt}
      className={className}
      style={computedStyle}
      onClick={onClick}
      onError={() => {
        const fallback = getDefaultBrandLogo();
        if (src !== fallback) {
          setSrc(fallback);
        }
      }}
    />
  );
}

BrandLogo.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
  alt: PropTypes.string,
  orgId: PropTypes.string
};

export default BrandLogo;
